import * as Sentry from '@sentry/react';
import {LEGAL_ROUTES} from '@sollapay/utils';
import {resolveLoginContextOnLoad} from '@sollapay/web-core';
import {lazy} from 'react';
import {Navigate, createBrowserRouter} from 'react-router-dom';

import {config} from '@/app';
import {RequireAuth} from '@/features/authentication';
import {AppShellSkeleton, GuestLayout, LegalLayout, MainLayout} from '@/layouts';
import {
  BuyerDetailPage,
  GuestEntryPage,
  GuestTrustAccountsPage,
  TeamMembersPage,
  TrustAccountActivationPage,
  TrustAccountListPage,
  TrustAccountViewPage
} from '@/pages';

import {LegacyTeamRedirect} from './LegacyTeamRedirect';

import type {RouteObject} from 'react-router-dom';

// Legal pages are code-split: they bundle the full legal markdown + react-markdown, which
// authenticated console users almost never open. Loaded on demand behind LegalLayout's Suspense.
const TermsOfUsePage = lazy(() =>
  import('@/pages/TermsOfUsePage').then(m => ({default: m.TermsOfUsePage}))
);
const PrivacyPolicyPage = lazy(() =>
  import('@/pages/PrivacyPolicyPage').then(m => ({default: m.PrivacyPolicyPage}))
);

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

// The account pages guests and members both open, so a shared link works for either.
const accountRoutes: RouteObject[] = [
  {
    path: '/trust-accounts/:id/activation',
    element: <TrustAccountActivationPage />
  },
  {
    path: '/trust-accounts/:id/buyers/:buyerId',
    element: <BuyerDetailPage />
  },
  {
    path: '/trust-accounts/:id/team',
    element: <LegacyTeamRedirect />
  },
  {
    path: '/trust-accounts/:id/:tab?',
    element: <TrustAccountViewPage />
  }
];

const memberShell: RouteObject = {
  element: <MainLayout />,
  children: [
    {
      path: '/',
      element: <Navigate to="/trust-accounts" replace />
    },
    {
      path: '/trust-accounts',
      element: <TrustAccountListPage />
    },
    ...accountRoutes,
    {
      path: '/team',
      element: <TeamMembersPage />
    },
    {
      path: '*',
      element: <Navigate to="/trust-accounts" replace />
    }
  ]
};

const guestShell: RouteObject = {
  element: <GuestLayout />,
  children: [
    {
      path: '/shared',
      element: <GuestTrustAccountsPage />
    },
    ...accountRoutes,
    {
      path: '*',
      element: <Navigate to="/shared" replace />
    }
  ]
};

// The mode is fixed for the page's life; switching it always goes through a full reload.
// A guest needs their own Auth0 client, so without one the session stays a member's (as in Auth0Provider).
const isGuestSession =
  Boolean(config.auth0.guestClientId) && resolveLoginContextOnLoad().kind === 'guest';
const appShell = isGuestSession ? guestShell : memberShell;

export const router = sentryCreateBrowserRouter([
  {
    path: '/guest',
    element: <GuestEntryPage />
  },
  {
    element: <LegalLayout />,
    children: [
      {
        path: LEGAL_ROUTES.termsOfUse,
        element: <TermsOfUsePage />
      },
      {
        path: LEGAL_ROUTES.privacyPolicy,
        element: <PrivacyPolicyPage />
      }
    ]
  },
  {
    element: <RequireAuth returningLoadingContent={<AppShellSkeleton />} />,
    children: [appShell]
  }
]);
